
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { QuestionMarkCircleIcon } from '@heroicons/react/outline';
import { ChatIcon, MailIcon } from '@heroicons/react/solid';
import { GetServerSidePropsContext } from 'next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import Script from 'next/script';
import useLocale from 'next-translate/useTranslation';
import React, { useEffect } from 'react';
import Button from '@/components/buttons/Button';
import InternalFaq from '@/components/InternalFaq';
import DashboardLayout from '@/components/layout/DashboardLayout';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import ButtonLink from '@/components/links/ButtonLink';
import UnstyledLink from '@/components/links/UnstyledLink';
import Seo from '@/components/Seo';
import { createClient } from '@/utils/supabase/server-props';
const Footer = dynamic(() => import('@/components/layout/Footer'));
interface SupportPageProps {
    isPublic?: boolean;
}
// Logged-out visitors (and crawlers, and users arriving from the iOS app's
// App Store support link) get the public marketing shell; signed-in users keep
// the dashboard chrome.
const SupportShell = ({ isPublic, isFromApp, children, }: {
    isPublic?: boolean;
    isFromApp?: boolean;
    children: React.ReactNode;
}) => {
    // Opened inside the iOS app: no marketing nav or footer. Both carry a
    // /pricing link, which is a purchase call-to-action App Store guideline
    // 3.1.3(b) does not allow to be reachable from within the app. The support
    // content itself is unchanged.
    if (isFromApp) {
        return (<div className='min-h-screen bg-gray-50 dark:bg-slate-900'>
        <div className='mx-auto w-full max-w-3xl px-2 sm:px-4'>{children}</div>
      </div>);
    }
    if (isPublic) {
        return (<div className='min-h-screen bg-gray-50 dark:bg-slate-900'>
        <LandingPageNavigation />
        <div className='mx-auto w-full max-w-7xl px-2 sm:px-4'>{children}</div>
        <Footer />
      </div>);
    }
    return <DashboardLayout>{children}</DashboardLayout>;
};
const Favorites = ({ isPublic }: SupportPageProps) => {
    const { t } = useLocale('support');
    const router = useRouter();
    // The iOS app links here as /support?from=app. See PURCHASE_CATEGORY_KEYS in
    // InternalFaq: those answers point at Stripe and /pricing, which must not be
    // reachable from inside the app (App Store guideline 3.1.3(b)).
    const isFromApp = router.query.from === 'app';
    const [buttonText, setButtonText] = React.useState<string>('Loading...');
    useEffect(() => {
        setTimeout(() => {
            setButtonText('Open Chat');
        }, 5000);
    }, []);
    return (<SupportShell isPublic={isPublic} isFromApp={isFromApp}>
      <main className='flex-1 overflow-y-auto focus:outline-none'>
        <Seo templateTitle={t('Help & Support')}/>
        {/* Crisp (human support). The site-wide AI support bubble
            (SiteChatWidget) is hidden on this page, so Crisp owns the corner:
            it starts hidden and only appears via the "Open Chat" button below. */}
        <Script id='chat' strategy='afterInteractive'>
          {`
window.$crisp=[["do","chat:hide"],["on","chat:closed",function(){$crisp.push(["do","chat:hide"]);}]];window.CRISP_WEBSITE_ID="25ba922c-77e9-4e8c-bcfb-b2b32ad48918";(function(){d=document;s=d.createElement("script");s.src="https://client.crisp.chat/l.js";s.async=1;d.getElementsByTagName("head")[0].appendChild(s);})();
          `}
        </Script>
        <div className='relative mx-auto md:px-8 xl:px-8 2xl:max-w-7xl'>
          <div className='pb-8 pt-10'>
            <div className='px-4 sm:px-6 md:px-0'>
              <h1 className='mb-10 text-3xl font-extrabold text-gray-900 dark:text-slate-200'>
                {t('Help & Support')}
              </h1>

              <div className='flex flex-col lg:flex-row lg:gap-8'>
                {/* FAQ Section - 75% width on large screens */}
                <div className='order-2 lg:order-1 lg:w-3/4'>
                  <InternalFaq hidePurchaseTopics={isFromApp}/>
                </div>

                {/* Support Options Sidebar - 25% width on large screens */}
                <div className='order-1 mb-8 lg:order-2 lg:mb-0 lg:w-1/4'>
                  <div className='lg:sticky lg:top-8'>
                    <div className='space-y-4'>
                      {/* Email Support */}
                      <div className='rounded-lg bg-white p-5 shadow-sm dark:bg-slate-800'>
                        <h3 className='flex items-center text-lg font-medium leading-6 text-gray-900 dark:text-slate-200'>
                          <MailIcon className='mr-2 h-6 w-6 text-gray-400 dark:text-slate-400' aria-hidden='true'/>
                          {t('Email us')}
                        </h3>
                        <dl className='mt-3 text-base text-gray-500'>
                          <div>
                            <dt className='sr-only'>{t('Email')}</dt>
                            <dd>
                              <UnstyledLink href='mailto:support@easy-peasy.ai' target='_blank' rel='noreferrer' className='text-primary-600 hover:text-primary-700'>
                                support@easy-peasy.ai
                              </UnstyledLink>
                            </dd>
                          </div>
                        </dl>
                      </div>

                      {/* Chat Support */}
                      <div className='rounded-lg bg-white p-5 shadow-sm dark:bg-slate-800'>
                        <h3 className='flex items-center text-lg font-medium leading-6 text-gray-900 dark:text-slate-200'>
                          <ChatIcon className='mr-2 h-6 w-6 text-gray-400 dark:text-slate-400' aria-hidden='true'/>
                          {t('Chat with Support')}
                        </h3>
                        <dl className='mt-3 text-base text-gray-500'>
                          <div>
                            <dt className='sr-only'>Chat</dt>
                            <dd>
                              <Button variant='outline' onClick={() => {
            // @ts-ignore
            $crisp.push(['do', 'chat:show']);
            // @ts-ignore
            $crisp.push(['do', 'chat:open']);
        }} className='w-full'>
                                {t(buttonText)}
                              </Button>
                            </dd>
                          </div>
                        </dl>
                      </div>

                      {/* Resources */}
                      <div className='rounded-lg bg-white p-5 shadow-sm dark:bg-slate-800'>
                        <h3 className='flex items-center text-lg font-medium leading-6 text-gray-900 dark:text-slate-200'>
                          <QuestionMarkCircleIcon className='mr-2 h-6 w-6 text-gray-400 dark:text-slate-400' aria-hidden='true'/>
                          {t('Resources')}
                        </h3>
                        <dl className='mt-3 space-y-2 text-base text-gray-500'>
                          <div>
                            <dt className='sr-only'>Guide</dt>
                            <dd>
                              <ButtonLink href='https://easy-peasy.ai/docs' target='_blank' variant='outline' className='w-full'>
                                {t('Open Guide')}
                              </ButtonLink>
                            </dd>
                          </div>
                          <div>
                            <dt className='sr-only'>Blog</dt>
                            <dd>
                              <ButtonLink href='https://easy-peasy.ai/blog' target='_blank' variant='outline' className='w-full'>
                                {t('Open Blog')}
                              </ButtonLink>
                            </dd>
                          </div>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </SupportShell>);
};
export default Favorites;
async function getServerSideProps(context: GetServerSidePropsContext) {
    const supabase = createClient(context);
    const { data: { user }, } = await supabase.auth.getUser();
    // The page used to 302 to /login, which made it unreachable for the support
    // URL published on the App Store listing. Logged-out visitors now get the
    // same FAQ + contact options on a public shell.
    if (!user) {
        return { props: { isPublic: true } };
    }
    return { props: {} };
}

    async function __Next_Translate__getServerSideProps__1a0e6093e93__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getServerSideProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/support',
            loaderName: 'getServerSideProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getServerSideProps__1a0e6093e93__ as getServerSideProps }
  